import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import PublicFrame, { usePublicBranding } from "./PublicFrame";

const today = () => new Date().toISOString().slice(0, 10);
const plus = (d) => new Date(Date.now() + d * 864e5).toISOString().slice(0, 10);
const SEL = "h-10 w-full rounded-md border bg-background px-3 text-sm";

export default function Waitlist() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", service_id: params.get("service") || "", location_id: params.get("location") || "",
    date_from: params.get("date") || today(), date_to: plus(14), time_pref: "any", consent: false });
  const [done, setDone] = useState(false);
  usePublicBranding(info);
  useEffect(() => { api.get(`/public/c/${slug}/clinic`).then((r) => setInfo(r.data)).catch(() => setInfo(false)); }, [slug]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = (e) => { e.preventDefault(); api.post(`/public/c/${slug}/waitlist`, f).then(() => setDone(true)).catch((err) => toast.error(errMsg(err))); };
  if (info === false) return <PublicFrame><p>Clinic not found.</p></PublicFrame>;
  const svcs = (info?.services || []).filter((s) => !f.location_id || s.prices?.[f.location_id]);
  return (
    <PublicFrame branding={info?.branding} embed={params.get("embed") === "1"}>
      {done ? (
        <div className="py-16 text-center space-y-3" data-testid="waitlist-success">
          <CheckCircle2 className="h-12 w-12 text-primary mx-auto" /><h1 className="text-3xl">You're on the waitlist</h1>
          <p className="text-muted-foreground">If a matching time becomes free we'll message you straight away. First to book gets it.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4 max-w-lg" data-testid="waitlist-form">
          <div><h1 className="text-3xl sm:text-4xl">Join the waitlist</h1><p className="text-muted-foreground mt-2">No time that suits you? Tell us what you'd like and we'll let you know as soon as a place opens up.</p></div>
          <select className={SEL} data-testid="waitlist-location" required value={f.location_id} onChange={set("location_id")}>
            <option value="">Choose a location…</option>{(info?.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
          <select className={SEL} data-testid="waitlist-service" required value={f.service_id} onChange={set("service_id")}>
            <option value="">Choose a treatment…</option>{svcs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">From<Input data-testid="waitlist-from" type="date" min={today()} value={f.date_from} onChange={set("date_from")} /></label>
            <label className="text-sm">To<Input data-testid="waitlist-to" type="date" min={f.date_from} value={f.date_to} onChange={set("date_to")} /></label>
          </div>
          <select className={SEL} data-testid="waitlist-time" value={f.time_pref} onChange={set("time_pref")}>
            <option value="any">Any time</option><option value="morning">Morning (before 12)</option><option value="afternoon">Afternoon (12–17)</option><option value="evening">Evening (after 17)</option></select>
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="waitlist-first-name" required placeholder="First name" value={f.first_name} onChange={set("first_name")} />
            <Input data-testid="waitlist-last-name" placeholder="Last name" value={f.last_name} onChange={set("last_name")} />
          </div>
          <Input data-testid="waitlist-email" type="email" required placeholder="Email" value={f.email} onChange={set("email")} />
          <Input data-testid="waitlist-phone" type="tel" placeholder="Mobile (for WhatsApp alerts)" value={f.phone} onChange={set("phone")} />
          <label className="flex gap-2 text-sm"><input type="checkbox" required data-testid="waitlist-consent" checked={f.consent} onChange={set("consent")} />Contact me by email or WhatsApp when a matching time becomes free.</label>
          <Button className="rounded-full w-full" data-testid="waitlist-submit">Join the waitlist</Button>
        </form>
      )}
    </PublicFrame>
  );
}
